/* eslint-disable no-unused-vars */
import { ssoFieldTypes } from '../../utils/field-types';
import { formConstants } from '../../utils/constants/form-constants';
import * as formFieldsConfig from './form-fields-config';
import { isEmpty } from '../../utils/obj-utils';
import { formStyleConstants } from '../../utils/constants/form-style-constants';
import { createDateFromExisting } from '../../utils/date-utils';
import { isTextAndImageField } from './form-helpers/form-field-helpers';

const FormBuilder = ({
    formData,
    formReferences,
    verificationCode,
    countyData,
    stateData,
    countryData,
    handleChange,
    handleFieldChange,
    handleConfirmEmailChange,
    handleOldPasswordChange,
    handleConfirmPasswordChange,
    handleDateChange,
    handleMobilePhoneChange,
    handleCountryChange,
    handleCaptchaChange,
    handleFieldValidState,
    handleOnBlur,
    handleMastercardNumberChange,
    isReadOnly,
}) => {
    function buildForm() {
        if (isEmpty(formData)) {
            return null;
        }

        if (isEmpty(formReferences)) {
            formReferences = [];
        }

        /* eslint-disable-next-line */
        var array = Object.entries(formData).filter(([key, value]) => value != undefined && value.fieldType !== undefined);

        array.sort(([, a], [, b]) => {
            const firstItemOrderNo = getOrderNo(a);
            const secondItemOrderNo = getOrderNo(b);

            return firstItemOrderNo - secondItemOrderNo;
        });

        return array.filter(([key, value]) => value !== undefined && value.fieldType !== undefined)
            .map(([formDataKey, formDataValue]) => {
                const fieldName = formDataKey;

                if (isTextAndImageField(formDataKey)) {
                    formDataKey = formDataKey.substring(0, formDataKey.indexOf('-'));
                }

                const formField = formFieldsConfig.formFields[formDataKey];

                if (!formField) {
                    return null;
                }

                let fieldOptions = isEmpty(formDataValue.options) ? formDataValue.choices : formDataValue.options;

                if (formDataValue.fieldType === ssoFieldTypes.MobilePhone) {
                    const countryFormData = formData[formConstants.country];
                    const countries = countryFormData.options;
                    fieldOptions = countries;
                }

                const fieldProperties = getFieldProperties({
                    fieldType: formDataValue.fieldType,
                    fieldLabel: formDataValue.label,
                    fieldHeaderTitle: formDataValue.fieldHeaderTitle,
                    fieldHeaderText: formDataValue.fieldHeaderText,
                    isHidden: formDataValue.isHidden,
                    defaultValue: formDataValue.defaultValue,
                    fieldSuggestions: formDataValue.suggestions,
                    fieldOptions,
                    isDisabled: isReadOnly || formDataValue.isDisabled,
                    tooltipText: formDataValue.tooltipText,
                    imageSrc: formDataValue.imageSrc,
                    tag: formDataValue.tag
                }, fieldName, formReferences[fieldName]);
                return formField(fieldProperties);
            });

        function getOrderNo(item) {
            return item.orderNo !== undefined ? item.orderNo : Number.MAX_SAFE_INTEGER;
        }
    }

    function getFieldProperties({
        fieldType,
        fieldLabel,
        fieldHeaderTitle,
        fieldHeaderText,
        isHidden,
        defaultValue,
        fieldSuggestions,
        fieldOptions,
        isDisabled,
        tooltipText,
        imageSrc,
        tag
    }, fieldName, formReference) {
        if (fieldType === ssoFieldTypes.EmailAddress) {
            return {
                fieldName,
                autocompleteValue: 'email',
                formReference,
                fieldLabel,
                fieldHeaderTitle,
                fieldHeaderText,
                isDisabled,
                tooltipText,
                isHidden,
                defaultValue,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState,
                handleOnBlur
            };
        }

        if (fieldType === ssoFieldTypes.ConfirmEmailAddress) {
            return {
                fieldName,
                autocompleteValue: 'email',
                formReference,
                fieldLabel,
                isDisabled,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden: true,
                defaultValue,
                fieldChangeCallback: handleConfirmEmailChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.Username) {
            return {
                fieldName,
                autocompleteValue: 'username email',
                formReference,
                fieldLabel,
                isDisabled,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.Password) {
            return {
                fieldName,
                autocompleteValue: 'current-password new-password',
                formReference,
                fieldLabel,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.OldPassword) {
            return {
                fieldName,
                autocompleteValue: 'current-password',
                formReference,
                fieldLabel,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldChangeCallback: handleOldPasswordChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.ConfirmPassword) {
            return {
                fieldName,
                autocompleteValue: 'new-password',
                formReference,
                fieldLabel,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldChangeCallback: handleConfirmPasswordChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.FirstName) {
            return {
                fieldName,
                autocompleteValue: 'given-name',
                formReference,
                fieldLabel,
                isDisabled,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.LastName) {
            return {
                fieldName,
                autocompleteValue: 'family-name',
                formReference,
                isDisabled,
                fieldLabel,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.AddressLine1) {
            return {
                fieldName,
                autocompleteValue: 'address-line-1',
                formReference,
                fieldLabel,
                isDisabled,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.AddressLine2) {
            return {
                fieldName,
                autocompleteValue: 'address-line-2',
                formReference,
                fieldLabel,
                isDisabled,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.AddressLine3) {
            return {
                fieldName,
                autocompleteValue: 'address-line-3',
                formReference,
                isDisabled,
                fieldLabel,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.City) {
            return {
                fieldName,
                autocompleteValue: 'shipping locality',
                formReference,
                isDisabled,
                fieldLabel,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.MainAddress ||
            fieldType === ssoFieldTypes.ShippingAddress ||
            fieldType === ssoFieldTypes.BillingAddress ||
            fieldType === ssoFieldTypes.BecomeOneClubMember) {
            return {
                fieldName,
                formReference,
                fieldLabel,
                isDisabled,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.BecomeOneClubMemberInfo) {
            return {
                fieldName,
                fieldHeaderText,
                isHidden,
                fieldHeaderTitle
            };
        }

        if (fieldType === ssoFieldTypes.MainAddressLabel) {
            return {
                fieldName,
                fieldHeaderText,
                isDisabled,
                fieldHeaderTitle
            };
        }

        if (fieldType === ssoFieldTypes.Postcode) {
            return {
                fieldName,
                autocompleteValue: 'postal-code',
                formReference,
                fieldLabel,
                fieldHeaderTitle,
                isDisabled,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldSize: formStyleConstants.smallField,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.MobilePhone) {
            return {
                fieldName,
                autocompleteValue: 'tel',
                formReference,
                isDisabled,
                fieldLabel,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldOptions,
                fieldChangeCallback: handleMobilePhoneChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.Title) {
            return {
                fieldName,
                formReference,
                fieldLabel,
                fieldHeaderTitle,
                isDisabled,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldOptions,
                propertyForValue: formConstants.titlePropertyForValue,
                fieldSize: formStyleConstants.smallField,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.DateOfBirth) {
            return {
                fieldName,
                formReference,
                fieldLabel,
                isDisabled,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue: defaultValue === undefined || defaultValue === null ? {} : createDateFromExisting(defaultValue),
                fieldChangeCallback: handleDateChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.Country) {
            return {
                fieldName,
                formReference,
                fieldLabel,
                isDisabled,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldOptions,
                propertyForValue: formConstants.countryPropertyForValue,
                fieldChangeCallback: handleCountryChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.County) {
            return {
                fieldName,
                formReference,
                fieldLabel: countyData.label,
                fieldHeaderTitle,
                fieldHeaderText,
                isDisabled,
                isHidden,
                defaultValue,
                fieldOptions: countyData.availableCounties,
                propertyForValue: formConstants.countyPropertyForValue,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.State) {
            return {
                fieldName,
                formReference,
                isDisabled,
                fieldLabel: stateData.label,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldOptions: stateData.availableStates,
                propertyForValue: formConstants.statePropertyForValue,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.FavoritePlayer) {
            return {
                fieldName,
                formReference,
                fieldLabel,
                isDisabled,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                fieldSuggestions,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.Venues) {
            return {
                fieldName,
                formReference,
                isDisabled,
                fieldLabel,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                interestsData: { choices: fieldOptions, label: fieldLabel },
                name: formConstants.venues,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.OffersDetails) {
            const { offersDetails } = formData;
            return {
                fieldName,
                formReference,
                fieldHeaderTitle,
                isDisabled,
                fieldHeaderText,
                isHidden,
                defaultValue,
                offersDetails,
                fieldChangeCallback: handleChange,
                acceptOffersName: formConstants.acceptOffers,
                refuseOffersName: formConstants.refuseOffers,
            };
        }

        if (fieldType === ssoFieldTypes.Consent) {
            const { consentDetails } = formData;
            return {
                fieldName,
                fieldHeaderTitle,
                fieldHeaderText,
                isDisabled,
                isHidden,
                defaultValue,
                formReference,
                consentDetails
            };
        }

        if (fieldType === ssoFieldTypes.PrivacyPolicy) {
            const { privacyPolicy } = formData;
            return {
                fieldName,
                formReference,
                defaultValue,
                isDisabled,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                policy: privacyPolicy.policy,
                consentLabel: privacyPolicy.label,
                showConsent: privacyPolicy.showConsent,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.OneClubTermsAndServices) {
            const { oneClubTermsAndServices } = formData;
            return {
                fieldName,
                formReference,
                defaultValue,
                isDisabled,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                policy: oneClubTermsAndServices.policy,
                consentLabel: oneClubTermsAndServices.label,
                showConsent: oneClubTermsAndServices.showConsent,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.ContactDetails) {
            const { contactDetails } = formData;
            return { fieldName: contactDetails.fieldKey, contactDetails };
        }

        if (fieldType === ssoFieldTypes.VerificationCode) {
            return {
                fieldName,
                fieldLabel,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue: verificationCode,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.ChampionshipSection) {
            return {
                fieldName,
                formReference,
                fieldLabel,
                isDisabled,
                fieldHeaderTitle,
                fieldHeaderText,
                isHidden,
                defaultValue,
                interestsData: { choices: fieldOptions, label: fieldLabel },
                name: formConstants.championshipSection,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.ChannelSection) {
            return {
                fieldName,
                formReference,
                fieldLabel,
                fieldHeaderTitle,
                isDisabled,
                fieldHeaderText,
                isHidden,
                defaultValue,
                interestsData: { choices: fieldOptions, label: fieldLabel },
                name: formConstants.channelSection,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.InterestSection) {
            return {
                fieldName,
                formReference,
                fieldLabel,
                fieldHeaderTitle,
                isDisabled,
                fieldHeaderText,
                isHidden,
                defaultValue,
                interestsData: { choices: fieldOptions, label: fieldLabel },
                name: formConstants.interestSection,
                fieldChangeCallback: handleFieldChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.Captcha) {
            return {
                fieldName,
                formReference,
                isHidden,
                fieldChangeCallback: handleCaptchaChange,
                fieldValidStateCallback: handleFieldValidState
            };
        }

        if (fieldType === ssoFieldTypes.Mastercard) {
            const { mastercardLogo } = formData;
            return {
                fieldName,
                autocompleteValue: 'mastercard',
                formReference,
                fieldLabel,
                fieldHeaderTitle,
                fieldHeaderText,
                isDisabled,
                isHidden,
                defaultValue,
                fieldChangeCallback: handleMastercardNumberChange,
                fieldValidStateCallback: handleFieldValidState,
                mastercardLogo
            };
        }

        if (fieldType === ssoFieldTypes.TextAndImageTile) {
            return {
                fieldName,
                formReference,
                fieldHeaderTitle,
                isDisabled,
                fieldHeaderText,
                imageSrc,
                tag
            };
        }

        return {};
    }

    return buildForm();
};

export default FormBuilder;
